
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, Article, articleFromDB } from '../../../lib/supabase';

const NewsEn: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('All');
  const [selectedCountry, setSelectedCountry] = useState<string>('All');
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  // Load articles from Supabase
  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('date', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        const convertedArticles = data.map(articleFromDB);
        setArticles(convertedArticles);
      } else {
        setArticles([]);
      }
    } catch (error) {
      console.error('Loading articles failed:', error);
      setArticles([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter articles based on selected categories
  useEffect(() => {
    let filtered = articles;

    if (selectedIndustry !== 'All') {
      filtered = filtered.filter(
        article => article.columnType === 'industry' && article.category === selectedIndustry
      );
    }

    if (selectedCountry !== 'All') {
      filtered = filtered.filter(
        article => article.columnType === 'country' && article.category === selectedCountry
      );
    }

    if (selectedIndustry !== 'All' && selectedCountry !== 'All') {
      filtered = articles.filter(
        article =>
          (article.columnType === 'industry' && article.category === selectedIndustry) ||
          (article.columnType === 'country' && article.category === selectedCountry)
      );
    }

    filtered = filtered.sort(
      (a: Article, b: Article) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    setFilteredArticles(filtered);
  }, [articles, selectedIndustry, selectedCountry]);

  const handleArticleClick = (articleId: string) => {
    navigate(`/en/news/detail/${articleId}`);
  };

  const handleIndustryFilter = (industry: string) => {
    setSelectedIndustry(industry);
    if (industry !== 'All') {
      setSelectedCountry('All');
    }
  };

  const handleCountryFilter = (country: string) => {
    setSelectedCountry(country);
    if (country !== 'All') {
      setSelectedIndustry('All');
    }
  };

  // Generate unique category lists
  const industryCategories = useMemo(() => {
    const categories = ['All'];
    const industryArticles = articles.filter(article => article.columnType === 'industry');
    const uniqueCategories = [...new Set(industryArticles.map(article => article.category))];
    return categories.concat(uniqueCategories.sort());
  }, [articles]);

  const countryCategories = useMemo(() => {
    const categories = ['All'];
    const countryArticles = articles.filter(article => article.columnType === 'country');
    const uniqueCategories = [...new Set(countryArticles.map(article => article.category))];
    return categories.concat(uniqueCategories.sort());
  }, [articles]);

  // Translation helper
  const getCategoryTranslation = (category: string, type: 'industry' | 'country'): string => {
    if (type === 'industry') {
      const translations: { [key: string]: string } = {
        金融科技: 'FinTech',
        电子商务: 'E-commerce',
        医疗健康: 'Healthcare',
        教育: 'Education',
        制造业: 'Manufacturing',
        零售: 'Retail',
        物流: 'Logistics',
        政府: 'Government',
        数字经济: 'Digital Economy',
        新能源: 'New Energy',
      };
      return translations[category] || category;
    } else {
      const translations: { [key: string]: string } = {
        新加坡: 'Singapore',
        马来西亚: 'Malaysia',
        泰国: 'Thailand',
        印度尼西亚: 'Indonesia',
        越南: 'Vietnam',
        菲律宾: 'Philippines',
        中国: 'China',
        日本: 'Japan',
        韩国: 'South Korea',
      };
      return translations[category] || category;
    }
  };

  // Count helpers - 修复统计逻辑
  const getIndustryCategoryCount = (category: string) => {
    if (category === 'All') {
      return articles.length; // 显示所有文章数量
    }
    return articles.filter(
      article => article.columnType === 'industry' && article.category === category
    ).length;
  };

  const getCountryCategoryCount = (category: string) => {
    if (category === 'All') {
      return articles.length; // 显示所有文章数量
    }
    return articles.filter(
      article => article.columnType === 'country' && article.category === category
    ).length;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 flex items-center justify-center gap-2 md:h-20 bg-[#071C74] px-4 py-4 font-medium md:flex-none md:justify-start md:p-2 md:px-3">
        <div className="sticky top-0 flex h-7 items-center justify-between md:h-20 w-full max-w-7xl mx-auto">
          <div className="adi-header-left">
            <a href="/">
              <img
                alt="ADI Logo"
                width={125}
                height={56}
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3900b388860a5efa532a02080a385ec7.png"
                className="h-14 w-auto"
              />
            </a>
          </div>

          <div className="hidden md:flex items-center space-x-4 text-white">
            <div className="relative">
              <div className="flex bg-white/10 backdrop-blur-sm rounded-full p-1">
                <a
                  href="/zh/news"
                  className="px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  简体中文
                </a>
                <button className="px-4 py-2 text-white bg-white/20 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap">
                  English
                </button>
              </div>
            </div>

            <a href="/flanexus/en" className="px-4 py-2 text-white hover:text-blue-200 transition-colors whitespace-nowrap">
              Flanexus
            </a>

            <a href="/en/news" className="px-4 py-2 text-white hover:text-blue-200 transition-colors">
              News
            </a>

            <a
              href="/en/contact-us"
              className="px-4 py-2 border border-white rounded-full hover:bg-white hover:text-[#071C74] transition-colors text-white"
            >
              Contact Us
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative h-[400px] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://public.readdy.ai/ai/img_res/bc0c3b4702b2e34c67d226621748246a.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-blue-600/60"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex items-center">
          <div className="text-white">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">Latest News</h1>
            <p className="text-xl lg:text-2xl opacity-90 max-w-2xl">
              Deep insights into digital transformation trends in the Asia-Pacific region, providing
              cutting-edge industry analysis and market dynamics
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-6">
              {/* Industry Filter */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-building-line mr-2 text-purple-600"></i>
                  By Industry
                </h3>
                <div className="space-y-2">
                  {/* All Industries */}
                  <button
                    onClick={() => handleIndustryFilter('All')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                      selectedIndustry === 'All'
                        ? 'bg-purple-100 text-purple-800 border-2 border-purple-300 font-medium'
                        : 'bg-gray-50 text-gray-700 hover:bg-purple-50 hover:text-purple-700 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>All</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">({getIndustryCategoryCount('All')})</span>
                        {selectedIndustry === 'All' && <i className="ri-check-line text-purple-600"></i>}
                      </div>
                    </div>
                  </button>

                  {/* Specific Industries */}
                  {industryCategories
                    .filter(cat => cat !== 'All')
                    .map(category => (
                      <button
                        key={category}
                        onClick={() => handleIndustryFilter(category)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                          selectedIndustry === category
                            ? 'bg-purple-100 text-purple-800 border-2 border-purple-300 font-medium'
                            : 'bg-gray-50 text-gray-700 hover:bg-purple-50 hover:text-purple-700 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{getCategoryTranslation(category, 'industry')}</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">
                              ({getIndustryCategoryCount(category)})
                            </span>
                            {selectedIndustry === category && <i className="ri-check-line text-purple-600"></i>}
                          </div>
                        </div>
                      </button>
                    ))}
                </div>
              </div>

              {/* Country Filter */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="ri-global-line mr-2 text-orange-600"></i>
                  By Country/Region
                </h3>
                <div className="space-y-2">
                  {/* All Countries */}
                  <button
                    onClick={() => handleCountryFilter('All')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                      selectedCountry === 'All'
                        ? 'bg-orange-100 text-orange-800 border-2 border-orange-300 font-medium'
                        : 'bg-gray-50 text-gray-700 hover:bg-orange-50 hover:text-orange-700 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>All</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">({getCountryCategoryCount('All')})</span>
                        {selectedCountry === 'All' && <i className="ri-check-line text-orange-600"></i>}
                      </div>
                    </div>
                  </button>

                  {/* Specific Countries */}
                  {countryCategories
                    .filter(cat => cat !== 'All')
                    .map(category => (
                      <button
                        key={category}
                        onClick={() => handleCountryFilter(category)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                          selectedCountry === category
                            ? 'bg-orange-100 text-orange-800 border-2 border-orange-300 font-medium'
                            : 'bg-gray-50 text-gray-700 hover:bg-orange-50 hover:text-orange-700 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{getCategoryTranslation(category, 'country')}</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">
                              ({getCountryCategoryCount(category)})
                            </span>
                            {selectedCountry === category && <i className="ri-check-line text-orange-600"></i>}
                          </div>
                        </div>
                      </button>
                    ))}
                </div>
              </div>

              {/* Follow Us Section */}
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Follow Us</h3>
                <img
                  alt="WeChat QR Code"
                  className="w-32 h-32 mx-auto mb-4 rounded-lg"
                  src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/798e5ca3f4e230ffb5a56d977a1ca61a.jpeg"
                />
                <p className="text-sm text-gray-600 text-center">Scan to follow our WeChat</p>
                <p className="text-xs text-gray-500 text-center mt-1">Get latest news updates</p>
              </div>
            </div>
          </div>

          {/* Right Content - Articles */}
          <div className="lg:col-span-3">
            {/* Filter Status */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h2 className="text-2xl font-bold text-gray-900">Latest News</h2>
                  <span className="text-gray-500">Total {filteredArticles.length} articles</span>
                </div>
                {(selectedIndustry !== 'All' || selectedCountry !== 'All') && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Current filter:</span>
                    {selectedIndustry !== 'All' && (
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                        {selectedIndustry}
                      </span>
                    )}
                    {selectedCountry !== 'All' && (
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                        {selectedCountry}
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setSelectedIndustry('All');
                        setSelectedCountry('All');
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Articles List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading...</p>
                </div>
              </div>
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-gray-400 mb-4">
                  <i className="ri-file-text-line text-6xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Articles</h3>
                <p className="text-gray-600">
                  {selectedIndustry !== 'All' || selectedCountry !== 'All'
                    ? 'No articles found in this category'
                    : 'No published articles available'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredArticles.map(article => (
                  <a
                    key={article.id}
                    href={`/en/news/detail/${article.id}`}
                    className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
                  >
                    <div className="relative h-48 overflow-hidden bg-gray-100">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        onError={e => {
                          const target = e.target as HTMLImageElement;
                          target.src = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20article.category%20%20%20article.title%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%29%7D&width=800&height=400&seq=${article.id}&orientation=landscape`;
                        }}
                      />
                      <div className="absolute top-4 left-4">
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full ${
                            article.columnType === 'industry'
                              ? 'bg-purple-600 text-white'
                              : 'bg-orange-600 text-white'
                          }`}
                        >
                          {article.columnType === 'industry' ? 'Industry Insights' : 'Country Insights'}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center space-x-2 mb-3">
                        <span
                          className={`px-2 py-1 text-xs rounded ${
                            article.columnType === 'industry' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                          }`}
                        >
                          {article.category}
                        </span>
                        <span className="text-sm text-gray-500">{article.date}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-3">{article.summary}</p>
                      <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
                        Read More
                        <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsEn;
